import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import Swal from 'sweetalert2';

export default function TransferenciaAprobacionModalComponent({
  show = false,
  onHide = () => {},
  transferencia = null,
  onAprobar = () => {},
  onRechazar = () => {}
}) {
  const [comentario, setComentario] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!show) {
      setComentario('');
    }
  }, [show]);

  const handleAprobar = async () => {
    if (!comentario.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Comentario requerido',
        text: 'Por favor ingrese un comentario antes de aprobar'
      });
      return;
    }

    setLoading(true);
    try {
      const resultado = await onAprobar(transferencia.trn_id, comentario);
      if (!resultado.error) {
        Swal.fire({
          icon: 'success',
          title: 'Aprobado',
          text: 'La transferencia ha sido aprobada correctamente'
        });
        onHide();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRechazar = async () => {
    if (!comentario.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Comentario requerido',
        text: 'Por favor ingrese un motivo de rechazo'
      });
      return;
    }

    setLoading(true);
    try {
      const resultado = await onRechazar(transferencia.trn_id, comentario);
      if (!resultado.error) {
        Swal.fire({
          icon: 'success',
          title: 'Rechazado',
          text: 'La transferencia ha sido rechazada'
        });
        onHide();
      }
    } finally {
      setLoading(false);
    }
  };

  if (!show || !transferencia) return null;

  // Obtener datos de la gira y alumno de los JSON almacenados
  // Verificar si ya son objetos o si necesitan parsearse
  const datosGira = typeof transferencia.trn_datos_gira === 'string' 
    ? JSON.parse(transferencia.trn_datos_gira) 
    : (transferencia.trn_datos_gira || {});
    
  const datosCuotas = typeof transferencia.trn_datos_cuotas === 'string' 
    ? JSON.parse(transferencia.trn_datos_cuotas) 
    : (Array.isArray(transferencia.trn_datos_cuotas) ? transferencia.trn_datos_cuotas : []);

  // Calcular número de cuotas desde los datos
  const numeroCuotas = Array.isArray(datosCuotas) ? datosCuotas.length : 0;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Gestionar Transferencia</Modal.Title>
      </Modal.Header>
      
      <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        <Row className="mb-4">
          <Col md={6}>
            <h5>Información del Alumno y Gira</h5>
            <div className="mb-2">
              <strong>Alumno:</strong> {`${transferencia.alu_nombres || ''} ${transferencia.alu_apellidos || ''}`.trim() || 'N/A'}
            </div>
            
            <div className="mb-2">
              <strong>Gira:</strong> {transferencia.est_nombre  + ' - ' + (transferencia.gir_curso || 'N/A') || 'N/A'}
            </div>
            <div className="mb-2">
              <strong>Programa:</strong> {transferencia.pro_descripcion || 'N/A'}
            </div>
            <div className="mb-2">
              <strong>Tramo:</strong> {transferencia.tra_descripcion || 'N/A'}
            </div>
          </Col>

          <Col md={6}>
            <h5>Información del Pago</h5>
            <div className="mb-2">
              <strong>Código:</strong> <code>{transferencia.trn_codigo}</code>
            </div>
            <div className="mb-2">
              <strong>Referencia de Pago:</strong> {transferencia.trn_referencia_pago || 'Pendiente'}
            </div>
            <div className="mb-2">
              <strong>Fecha de Pago:</strong> {transferencia.trn_fecha_pago ? new Date(transferencia.trn_fecha_pago).toLocaleDateString('es-CL') : 'No pagada'}
            </div>
            <div className="mb-2">
              <strong>¿En Dólares?:</strong> {transferencia.gir_pagodolares === true || transferencia.gir_pagodolares === 1 || transferencia.gir_pagodolares?.data?.[0] === 1 ? 'Sí' : 'No'}
            </div>
          </Col>
        </Row>

        <Row className="mb-4 p-3 bg-light rounded">
          <Col md={6}>
            <div className="mb-3">
              <strong>Monto del Pago:</strong>
              <div style={{ fontSize: '1.5rem', color: '#007bff' }}>
                ${Number(transferencia.trn_monto).toLocaleString('es-CL', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </div>
            </div>
          </Col>

          {transferencia.trn_multa > 0 && (
            <Col md={6}>
              <div className="mb-3">
                <strong>Multa Aplicada:</strong>
                <div style={{ fontSize: '1.5rem', color: '#dc3545' }}>
                  ${Number(transferencia.trn_multa).toLocaleString('es-CL', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </div>
              </div>
            </Col>
          )}

          <Col md={6}>
            <div>
              <strong>Número de Cuotas:</strong>
              <div style={{ fontSize: '1.2rem' }}>
                {numeroCuotas}
              </div>
            </div>
          </Col>
        </Row>

        <Row className="mb-4">
          <Col xs={12}>
            <h5>Detalle de Cuotas</h5>
            <div className="table-responsive" style={{ maxHeight: '200px', overflowY: 'auto' }}>
              <table className="table table-sm table-bordered">
                <thead>
                  <tr>
                    <th>Número</th>
                    <th>Monto</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {datosCuotas && datosCuotas.map((cuota, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>${cuota.cou_valor?.toLocaleString('es-CL')}</td>
                      <td>{cuota.cuo_estado || 'Pendiente'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Col>
        </Row>

        <Form.Group>
          <Form.Label><strong>Comentario</strong> <span style={{color: 'red'}}>*</span></Form.Label>
          <Form.Control
            as="textarea"
            rows={4}
            placeholder="Ingrese un comentario (aprobación o motivo de rechazo)"
            value={comentario}
            onChange={(e) => setComentario(e.target.value)}
            disabled={loading}
          />
          <Form.Text className="text-muted">
            Este comentario será registrado en la auditoría
          </Form.Text>
        </Form.Group>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={onHide}
          disabled={loading}
        >
          Cancelar
        </Button>
        <Button
          variant="danger"
          onClick={handleRechazar}
          disabled={loading}
        >
          {loading ? 'Procesando...' : 'Rechazar'}
        </Button>
        <Button
          variant="success"
          onClick={handleAprobar}
          disabled={loading}
        >
          {loading ? 'Procesando...' : 'Aprobar'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
